'use client'

import { InfoCircle16, Plus24 } from '@vinted/monochrome-icons'
import { Button, Cell, Checkbox, Icon, Spacer, Text } from '@vinted/web-ui'
import { Fragment, useEffect, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'

import { useAsset } from '@marketplace-web/shared/assets'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import InformationModal from '../../InformationModal'
import UboAddressDetails from './UboAddressDetails'
import UboFields from './UboFields'

const MAX_UBO_COUNT = 4

type Props = {
  isLegalRepresentativeUbo: boolean
  isFormDisabled: boolean
  setIsLegalRepresentativeUbo: (payload: boolean) => void
}

const Ubo = ({ isFormDisabled, isLegalRepresentativeUbo, setIsLegalRepresentativeUbo }: Props) => {
  const translate = useTranslate('kyc_form.business.ubo')
  const asset = useAsset('/assets/kyc')

  const [isUboInformationVisible, setIsUboInformationVisible] = useState(false)

  const { control, setValue, clearErrors } = useFormContext()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ubos',
  })

  function handleUboTypeSelection() {
    setIsLegalRepresentativeUbo(!isLegalRepresentativeUbo)
  }

  useEffect(() => {
    setValue('ubos', [{}])
    clearErrors('ubos')
  }, [isLegalRepresentativeUbo, setValue, clearErrors])

  const removeUbo = (index: number) => () => remove(index)

  function addUbo() {
    append({})
  }

  function handleInfoIconClick() {
    setIsUboInformationVisible(true)
  }

  function handleUboInformationModalClose() {
    setIsUboInformationVisible(false)
  }

  function renderUboTypeSelection() {
    return (
      <>
        <Cell
          styling={Cell.Styling.Wide}
          body={<Text as="h3" type={Text.Type.Subtitle} text={translate('declaration.header')} />}
          suffix={
            <Button
              icon={
                <Icon
                  color={Icon.Color.GreyscaleLevel3}
                  name={InfoCircle16}
                  aria={{ 'aria-hidden': 'true' }}
                />
              }
              styling={Button.Styling.Flat}
              inline
              size={Button.Size.Small}
              onClick={handleInfoIconClick}
            />
          }
          disabled={isFormDisabled}
        />
        <Cell
          body={
            <Checkbox
              text={translate('declaration.type')}
              name="fill_all_ubo"
              onClick={handleUboTypeSelection}
              checked={isLegalRepresentativeUbo}
              onChange={() => undefined}
            />
          }
          disabled={isFormDisabled}
        />
        <Spacer size={Spacer.Size.XLarge} />
      </>
    )
  }

  function renderAddUbo(currentIndex: number) {
    if (fields.length === MAX_UBO_COUNT) return null

    if (currentIndex !== fields.length - 1) return null

    return (
      <>
        <Spacer size={Spacer.Size.XLarge} />
        <Cell
          styling={Cell.Styling.Tight}
          theme="transparent"
          suffix={
            <Button
              size={Button.Size.Medium}
              icon={
                <Icon name={Plus24} color={Icon.Color.Primary} aria={{ 'aria-hidden': 'true' }} />
              }
              text={translate('add')}
              onClick={addUbo}
            />
          }
          disabled={isFormDisabled}
        />
      </>
    )
  }

  function renderRemoveUbo(currentIndex: number) {
    if (currentIndex === 0) return null

    return (
      <Button
        size={Button.Size.Medium}
        theme="warning"
        text={translate('delete')}
        onClick={removeUbo(currentIndex)}
      />
    )
  }

  function renderMinimalUbo() {
    return (
      <>
        <Cell
          styling={Cell.Styling.Wide}
          body={<Text as="h3" type={Text.Type.Subtitle} text={translate('minimal.header')} />}
          disabled={isFormDisabled}
        />
        <Cell
          styling={Cell.Styling.Tight}
          body={<UboFields index={0} isMinimal />}
          disabled={isFormDisabled}
        />
      </>
    )
  }

  function renderFullUbos() {
    return fields.map((field, index) => (
      <Fragment key={field.id}>
        <Spacer size={Spacer.Size.XLarge} />
        <Cell
          styling={Cell.Styling.Tight}
          theme="transparent"
          prefix={<Text as="h2" type={Text.Type.Title} text={translate(`list.${index + 1}_ubo`)} />}
          suffix={renderRemoveUbo(index)}
          disabled={isFormDisabled}
        />
        <Spacer size={Spacer.Size.Large} />
        <Cell
          styling={Cell.Styling.Wide}
          body={<Text as="h3" type={Text.Type.Subtitle} text={translate('general.title')} />}
          disabled={isFormDisabled}
        />
        <Cell styling={Cell.Styling.Tight} disabled={isFormDisabled}>
          <UboFields index={index} isMinimal={false} />
        </Cell>
        <Spacer size={Spacer.Size.XLarge} />

        <UboAddressDetails index={index} isFormDisabled={isFormDisabled} />

        {renderAddUbo(index)}
      </Fragment>
    ))
  }

  function renderInformationModal() {
    const labels = {
      title: translate('information_modal.title'),
      description: translate('information_modal.description'),
      closeButtonText: translate('information_modal.buttons.got_it'),
    }

    return (
      <InformationModal
        {...labels}
        show={isUboInformationVisible}
        iconPath={asset('lamp.svg')}
        onClose={handleUboInformationModalClose}
      />
    )
  }

  return (
    <>
      {renderUboTypeSelection()}
      {isLegalRepresentativeUbo ? renderMinimalUbo() : renderFullUbos()}
      {renderInformationModal()}
    </>
  )
}

export default Ubo
